import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import AuthLayout, { AuthInput, AuthLabel, AuthError, AuthLink } from '../components/ui/AuthLayout';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { updatePassword, logout } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Check if recovery session or token is active in Supabase
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      // Supabase parses hash fragment (#access_token=...&type=recovery) automatically
      const hash = window.location.hash;
      const isRecovery = hash.includes('type=recovery') || !!session;
      setReady(true);
      if (!isRecovery && !session) {
        setError('Lidhja e rivendosjes mungon ose ka skaduar. Ju lutem kërkoni një lidhje të re.');
      }
    };

    checkSession();
  }, []);

  const passwordsMatch = !confirmPassword || password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('Fjalëkalimi duhet të ketë të paktën 6 karaktere.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Fjalëkalimet nuk përputhen.');
      return;
    }

    setLoading(true);
    setError('');

    const res = await updatePassword(password);
    setLoading(false);

    if (res.success) {
      setSuccess(true);
      setTimeout(async () => {
        await logout();
        navigate('/login');
      }, 2500);
    } else {
      setError(res.error || 'Dështoi përditësimi i fjalëkalimit. Lidhja mund të ketë skaduar.');
    }
  };

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400" />
      </div>
    );
  }

  return (
    <AuthLayout
      title="Vendos fjalëkalimin e ri"
      subtitle="Zgjidhni një fjalëkalim të sigurt për llogarinë tuaj."
      footer={
        <>
          Kujtuat fjalëkalimin? <AuthLink to="/login">Kthehu te hyrja</AuthLink>
        </>
      }
    >
      {success ? (
        <div className="space-y-5">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 text-sm text-emerald-900 flex items-start gap-3">
            <CheckCircleIcon className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1">Fjalëkalimi u ndryshua me sukses!</p>
              <p className="text-emerald-700 text-xs leading-relaxed">
                Po ju ridrejtojmë te faqja e hyrjes...
              </p>
            </div>
          </div>
          <Button asChild variant="outline" className="w-full h-10 border-zinc-200 hover:bg-zinc-100 text-zinc-900 font-medium">
            <Link to="/login">Shko te hyrja menjëherë</Link>
          </Button>
        </div>
      ) : (
        <form className="space-y-5" onSubmit={handleSubmit}>
          {error && <AuthError>{error}</AuthError>}

          {/* New Password */}
          <div className="space-y-1.5">
            <AuthLabel htmlFor="password">Fjalëkalimi i ri</AuthLabel>
            <div className="relative">
              <AuthInput
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                icon={LockClosedIcon}
                autoComplete="new-password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600"
              >
                {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="space-y-1.5">
            <AuthLabel htmlFor="confirmPassword">Konfirmo fjalëkalimin</AuthLabel>
            <AuthInput
              id="confirmPassword"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              icon={LockClosedIcon}
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className={cn(!passwordsMatch && 'border-red-400 focus:border-red-500')}
            />
            {!passwordsMatch && (
              <p className="text-xs font-medium text-red-600 mt-1">Fjalëkalimet nuk përputhen</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading || !passwordsMatch || !password}
            className="w-full h-11 bg-black hover:bg-zinc-800 text-white font-bold rounded-xl text-sm transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Duke ruajtur...
              </span>
            ) : (
              'Ruaj fjalëkalimin e ri'
            )}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
};

export default ResetPasswordPage;
